import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, isValid } from 'date-fns';
import { createContractor } from '../services/contractorsService';
import S3ImageUpload from '../components/S3ImageUpload';
import styles from '../assets/contractors.module.css';

const parseDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isValid(date) ? date : null;
};

const ContractorCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contractor_id: '',
    email: '',
    email_alternative: '',
    logo: null,
    logoKey: '',
    logoName: '',
    logoType: '',
    logoSize: 0,
    date_review: '',
    address_1: '',
    address_2: '',
    address_3: '',
    town: '',
    postcode: '',
    phone_number: '',
    phone_number_alternative: '',
    fax_number: '',
    web: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Input changed: ${name} = "${value}"`);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageUpload = (imageKey, file) => {
    setFormData(prev => ({
      ...prev,
      logoKey: imageKey || '',
      logoName: file ? file.name : '',
      logoType: file ? file.type : '',
      logoSize: file ? file.size : 0,
      logo: file || null
    }));
    
    // Clear logo error when new image is uploaded
    if (errors.logo) {
      setErrors(prev => ({
        ...prev,
        logo: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.contractor_id.trim()) {
      newErrors.contractor_id = 'Contractor ID is required';
    }

    // Max length validation (matching Laravel CreateContractorRequest)
    const maxLengths = {
      name: 255,
      contractor_id: 255,
      email: 100,
      email_alternative: 100,
      address_1: 255,
      address_2: 255,
      address_3: 255,
      town: 255,
      postcode: 255,
      phone_number: 255,
      phone_number_alternative: 255,
      fax_number: 255,
      web: 255,
    };

    for (const [field, maxLength] of Object.entries(maxLengths)) {
      if (formData[field] && formData[field].length > maxLength) {
        newErrors[field] = `The ${field.replace(/_/g, " ")} must not be greater than ${maxLength} characters.`;
      }
    }

    // Email format validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.email_alternative && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_alternative)) {
      newErrors.email_alternative = 'Please enter a valid email address';
    }

    // Logo validation is handled by S3ImageUpload component

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug: Log current form data
    console.log('Form Data before submission:', formData);
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Prepare JSON data for submission
      const submitData = {
        ...formData
      };
      
      // Remove File object and other frontend-specific fields that shouldn't be sent to backend
      delete submitData.logo;

      // Debug: Log data being sent to backend
      console.log('Data being sent to backend:', submitData);

      const response = await createContractor(submitData);
      
      if (response.success) {
        toast.success('Contractor created successfully!');
        // Navigate to contractor show page (matching Laravel redirect()->route('contractors.show', $contractor->id))
        if (response.data && response.data.id) {
          navigate(`/contractors/${response.data.id}`);
        } else {
          navigate('/contractors');
        }
      } else {
        // Handle field-level errors from backend (matching Laravel validation errors)
        if (response.errors) {
          // Convert Laravel-style errors (object with arrays) to simple object with strings
          const formattedErrors = {};
          for (const [field, messages] of Object.entries(response.errors)) {
            formattedErrors[field] = Array.isArray(messages) ? messages[0] : messages;
          }
          setErrors(formattedErrors);
          toast.error(response.message || 'Validation failed');
        } else {
          toast.error(response.message || 'Failed to create contractor');
        }
      }
    } catch (error) {
      console.error('Error creating contractor:', error);
      toast.error('Error creating contractor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.contractorsContainer}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <Link to="/dashboard" className={styles.breadcrumbItem}>Dashboard</Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <Link to="/contractors" className={styles.breadcrumbItem}>Contractors</Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbItem + ' ' + styles.active}>Create</span>
      </nav>

      <div className="row">
        <div className="col-md-12">
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Add Contractor</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} encType="multipart/form-data">
                <fieldset className={styles.formHorizontal}>
                  {/* Contractor Name */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Name <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${errors.name ? styles.hasError : ''}`}
                        required
                      />
                      {errors.name && <span className={styles.errorMessage}>{errors.name}</span>}
                    </div>
                  </div>

                  {/* Contractor ID */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Contractor ID <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="contractor_id"
                        value={formData.contractor_id}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${errors.contractor_id ? styles.hasError : ''}`}
                        required
                      />
                      {errors.contractor_id && <span className={styles.errorMessage}>{errors.contractor_id}</span>}
                    </div>
                  </div>

                  {/* Email */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Email</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${errors.email ? styles.hasError : ''}`}
                      />
                      {errors.email && <span className={styles.errorMessage}>{errors.email}</span>}
                    </div>
                  </div>

                  {/* Alternative Email */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Alternative Email</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="email"
                        name="email_alternative"
                        value={formData.email_alternative}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${errors.email_alternative ? styles.hasError : ''}`}
                      />
                      {errors.email_alternative && <span className={styles.errorMessage}>{errors.email_alternative}</span>}
                    </div>
                  </div>

                  {/* Logo */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Logo</label>
                    <div className={styles.inputGroup}>
                      <S3ImageUpload
                        onImageUpload={handleImageUpload}
                        currentImage={formData.logoKey}
                        accept="image/*"
                        maxSize={100 * 1024 * 1024} // 100MB
                        className={styles.formControl}
                        helpText="The logo must be a clear image of your company logo. Allowed formats: JPEG, PNG, GIF, WebP, BMP. Maximum file size: 100MB"
                        showPreview={true}
                        exampleImage={null}
                        folder="uploads/images/contractors"
                      />
                      {errors.logo && <span className={styles.errorMessage}>{errors.logo}</span>}
                    </div>
                  </div>

                  {/* Review Date */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Review Date</label>
                    <div className={styles.inputGroup}>
                      <DatePicker
                        selected={parseDate(formData.date_review)}
                        onChange={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            date_review:
                              date && isValid(date)
                                ? format(date, 'yyyy-MM-dd')
                                : '',
                          }))
                        }
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd/mm/yyyy"
                        className={styles.formControl}
                        autoComplete="off"
                        isClearable
                      />
                    </div>
                  </div>

                  {/* Address 1 */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Address 1</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="address_1"
                        value={formData.address_1}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Address 2 */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Address 2</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="address_2"
                        value={formData.address_2}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Address 3 */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Address 3</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="address_3"
                        value={formData.address_3}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Town */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Town</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="town"
                        value={formData.town}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Postcode */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Postcode</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Phone Number</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Alternative Phone Number */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Alternative Phone Number</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="phone_number_alternative"
                        value={formData.phone_number_alternative}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Fax Number */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Fax Number</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="fax_number"
                        value={formData.fax_number}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Web */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Web</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="url"
                        name="web"
                        value={formData.web}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Notes</label>
                    <div className={styles.inputGroup}>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        rows="4"
                      />
                    </div>
                  </div>

                  {/* Information Help Text */}
                  <fieldset className={styles.formHorizontal} style={{ marginTop: '30px' }}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabel}>Information</label>
                      <div className={styles.inputGroup}>
                        <div className={styles.helpText}>
                          After you create a contractor you may add services and contractor contacts.
                        </div>
                      </div>
                    </div>
                  </fieldset>

                  {/* Submit Button */}
                  <fieldset className={styles.formHorizontal}>
                    <div className={styles.formGroup}>
                      <div className={styles.inputGroup}>
                        <button
                          type="submit"
                          className={styles.btnHead}
                          disabled={loading}
                        >
                          <i className={styles.glyphiconPlus}></i>
                          {loading ? 'Creating...' : 'Add Contractor'}
                        </button>
                      </div>
                    </div>
                  </fieldset>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorCreate; 